import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { AppState } from '@nxsys/store';
import { ICompany } from '@nxsys/types/models/company';
import { showCurrencyByCountry } from '@nxsys/utils';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppCheckbox from '@nxsys/components/AppCheckbox';
import { Form, Row, Col, Typography } from 'antd';
import { useSelector } from 'react-redux';
import { useImperativeHandle, Ref, useEffect, useState } from 'react';
import IndustryTypeModal from './IndustryTypeModal';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';

type EmployerSettingsFormRef = {
  submit: () => void;
};

export type EmployerSettingsFormValues = {
  taxDistrict: string;
  accountsOfficeReference: string;
  employeeReference: string;
  claimEmpAllowance: boolean;
  allowanceClaimableEmpAllowance: string;
  allowanceClaimedSoFar: string;
  apprenticeLevyDue: boolean;
  eligibleForSmallEmpRelief: boolean;
  allowZeroOrNegativePayslip: boolean;
  allowanceClaimableAppLevy: string;
  deductionRate: string;
  recoverableAmountsYTD: boolean;
  smpRecoveredYTD: string;
  sppRecoveredYTD: string;
  sapRecoveredYTD: string;
  shppRecoveredYTD: string;
  spbpRecoveredYTD: string;
  sncpRecoveredYTD: string;
  nicCompensationOnSmpYTD: string;
  nicCompensationOnSppYTD: string;
  nicCompensationOnSapYTD: string;
  nicCompensationOnShppYTD: string;
  nicCompensationOnSpbpYTD: string;
  nicCompensationOnSncpYTD: string;
  cisDeductionsSufferedYTD: string;
  appLevyClaimedYTD: string;
};

type EmployerSettingsFormProps = {
  companyData?: ICompany;
  formRef?: Ref<EmployerSettingsFormRef>;
  onSubmit?: (values: EmployerSettingsFormValues) => void;
};

const EmployerSettingsForm: React.FC<EmployerSettingsFormProps> = ({
  companyData,
  formRef,
  onSubmit,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);
  const [isOpenIndustryTypeModal, setIsOpenIndustryTypeModal] = useState(false);

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  const onFinish = (values: any) => {
    // Strip removed fields to prevent BE "invalid numeric" errors
    const { allowanceClaimableEmpAllowance, allowanceClaimableAppLevy, deductionRate, ...cleanValues } = values;
    onSubmit?.(cleanValues);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  const recoverableAmountsYTD = Form.useWatch('recoverableAmountsYTD', form);

  useEffect(() => {
    if (companyData?.empSetting && needReinitializeForm) {
      const initialValues = companyData?.empSetting
        ? {
            taxDistrict: companyData.empSetting.taxDistrict || '',
            accountsOfficeReference:
              companyData.empSetting.accountsOfficeReference || '',
            employeeReference: companyData.empSetting.employeeReference || '',
            claimEmpAllowance:
              companyData.empSetting.claimEmpAllowance || false,
            apprenticeLevyDue:
              companyData.empSetting.apprenticeLevyDue || false,
            eligibleForSmallEmpRelief:
              companyData.empSetting.eligibleForSmallEmpRelief || false,
            allowZeroOrNegativePayslip:
              companyData.empSetting.allowZeroOrNegativePayslip || false,
            allowanceClaimedSoFar:
              companyData.empSetting.allowanceClaimedSoFar || '',
            recoverableAmountsYTD:
              companyData.empSetting.recoverableAmountsYTD || false,
            smpRecoveredYTD: companyData.empSetting.smpRecoveredYTD || '',
            sppRecoveredYTD: companyData.empSetting.sppRecoveredYTD || '',
            sapRecoveredYTD: companyData.empSetting.sapRecoveredYTD || '',
            shppRecoveredYTD: companyData.empSetting.shppRecoveredYTD || '',
            spbpRecoveredYTD: companyData.empSetting.spbpRecoveredYTD || '',
            sncpRecoveredYTD: companyData.empSetting.sncpRecoveredYTD || '',
            nicCompensationOnSmpYTD:
              companyData.empSetting.nicCompensationOnSmpYTD || '',
            nicCompensationOnSppYTD:
              companyData.empSetting.nicCompensationOnSppYTD || '',
            nicCompensationOnSapYTD:
              companyData.empSetting.nicCompensationOnSapYTD || '',
            nicCompensationOnShppYTD:
              companyData.empSetting.nicCompensationOnShppYTD || '',
            nicCompensationOnSpbpYTD:
              companyData.empSetting.nicCompensationOnSpbpYTD || '',
            nicCompensationOnSncpYTD:
              companyData.empSetting.nicCompensationOnSncpYTD || '',
            cisDeductionsSufferedYTD:
              companyData.empSetting.cisDeductionsSufferedYTD || '',
            appLevyClaimedYTD:
              companyData.empSetting.appLevyClaimedYTD || '',
          }
        : {};
      form.setFieldsValue(initialValues);
      setNeedReinitializeForm(false);
    }
  }, [companyData?.empSetting]);

  const handleClickHMRCGuidelines = (
    e: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    e.preventDefault();
    setIsOpenIndustryTypeModal(true);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      {/* HMRC Settings */}
      <div className="mb-6">
        <Row gutter={24}>
          <Col sm={24} md={6}>
            <Form.Item
              label={messages['common.taxDistrict']}
              name="taxDistrict"
            >
              <AppInput placeholder={messages['form.enter']} maxLength={3} />
            </Form.Item>
          </Col>
          <Col sm={24} md={9}>
            <Form.Item
              label={messages['table.employerReference'] ?? 'Employer Reference'}
              name="employeeReference"
            >
              <AppInput placeholder={messages['form.enter']} />
            </Form.Item>
          </Col>
          <Col sm={24} md={9}>
            <Form.Item
              label={messages['common.accountsOfficeReference']}
              name="accountsOfficeReference"
            >
              <AppInput placeholder={messages['form.enter']} maxLength={13} />
            </Form.Item>
          </Col>
        </Row>
      </div>

      <Row gutter={24} className="mb-6">
        {/* Left column: Claim Employment Allowance + Apprentice Levy Due */}
        <Col sm={24} md={15}>
          {/* Claim Employment Allowance */}
          <div className="mb-6">
            <Form.Item
              name="claimEmpAllowance"
              valuePropName="checked"
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              className="mb-0"
            >
              <AppCheckbox>
                <div>
                  <div className="text-[14px]">
                    {messages['common.claimEmpAllowance']}
                  </div>
                  <div className="text-[12px]">
                    {messages['common.visitHMRCGuidelines']}{' '}
                    <Typography.Link
                      style={{
                        fontSize: 12,
                        color: defaultTheme.theme.palette.primary.main,
                      }}
                      onClick={handleClickHMRCGuidelines}
                    >
                      {messages['common.hmrcGuidelines']}
                    </Typography.Link>{' '}
                    {messages['common.visitHMRCGuidelinesSuffix']}
                  </div>
                </div>
              </AppCheckbox>
            </Form.Item>
          </div>

          {/* Apprentice Levy Due */}
          <Form.Item
            name="apprenticeLevyDue"
            valuePropName="checked"
            labelCol={{ span: 0 }}
            wrapperCol={{ span: 24 }}
            className="mb-0"
          >
            <AppCheckbox>{messages['common.apprenticeLevyDue']}</AppCheckbox>
          </Form.Item>
        </Col>

        {/* Right column: Small Employer's Relief (SER) */}
        <Col sm={24} md={9}>
          <Form.Item
            name="eligibleForSmallEmpRelief"
            valuePropName="checked"
            labelCol={{ span: 0 }}
            wrapperCol={{ span: 24 }}
            className="mb-0"
          >
            <AppCheckbox>
              <div>
                <div className="text-[14px]">
                  {messages['common.eligibleForSmallEmpRelief']}
                </div>
                {/* <div className="text-[12px]">
                  {messages['common.eligibleForSmallEmpReliefHelp']}
                </div> */}
              </div>
            </AppCheckbox>
          </Form.Item>

          {/* Allow Zero/Negative payslip — bureau + UK only */}
          {isUK && (
            <Form.Item
              name="allowZeroOrNegativePayslip"
              valuePropName="checked"
              labelCol={{ span: 0 }}
              wrapperCol={{ span: 24 }}
              className="mb-0 mt-6"
            >
              <AppCheckbox>
                <div>
                  <div className="text-[14px]">
                    {messages['common.allowZeroOrNegativePayslip']}
                  </div>
                </div>
              </AppCheckbox>
            </Form.Item>
          )}
        </Col>
      </Row>

      {/* Recoverable Amounts YTD */}
      <div className="mb-6">
        <Form.Item
          name="recoverableAmountsYTD"
          valuePropName="checked"
          labelCol={{ span: 0 }}
          wrapperCol={{ span: 24 }}
        >
          <AppCheckbox>
            {messages['common.recoverableAmountsYTD'] ||
              'Recoverable Amounts YTD?'}
          </AppCheckbox>
        </Form.Item>
        {recoverableAmountsYTD && (
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Emp Allowance Claimed YTD"
                name="allowanceClaimedSoFar"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.smpRecoveredYTD']}
                name="smpRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.sapRecoveredYTD']}
                name="sapRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.spbpRecoveredYTD']}
                name="spbpRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnSmpYTD']}
                name="nicCompensationOnSmpYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnSapYTD']}
                name="nicCompensationOnSapYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnSpbpYTD']}
                name="nicCompensationOnSpbpYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.cisDeductionsSufferedYTD']}
                name="cisDeductionsSufferedYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={messages['common.sppRecoveredYTD']}
                name="sppRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.shppRecoveredYTD']}
                name="shppRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.sncpRecoveredYTD']}
                name="sncpRecoveredYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnSppYTD']}
                name="nicCompensationOnSppYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnShppYTD']}
                name="nicCompensationOnShppYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.nicCompensationOnSncpYTD']}
                name="nicCompensationOnSncpYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
              <Form.Item
                label={messages['common.appLevyClaimedYTD']}
                name="appLevyClaimedYTD"
              >
                <AppCurrencyInput
                  placeholder={messages['form.enter']}
                  prefix={showCurrencyByCountry(payrollCountry)}
                />
              </Form.Item>
            </Col>
          </Row>
        )}
      </div>

      {/* Industry Type */}
      {isOpenIndustryTypeModal && (
        <IndustryTypeModal
          isOpen={isOpenIndustryTypeModal}
          toggle={() => setIsOpenIndustryTypeModal(!isOpenIndustryTypeModal)}
          data={companyData}
        />
      )}
    </AppFormContainer>
  );
};

export default EmployerSettingsForm;
